import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Loader2,
  ShieldCheck,
} from "lucide-react";
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

const MOOD_EMOJI = {
  focused: "🎯",
  proud: "🏆",
  grateful: "🙏",
  calm: "😌",
  tired: "😴",
  scattered: "🌀",
  frustrated: "😤",
  overwhelmed: "🥵",
  energized: "⚡",
  drained: "🪫",
};

const moodEmoji = (m) => (m && MOOD_EMOJI[m.toLowerCase()]) || (m ? "•" : "");

export default function EnergyTrend() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/energy-trend`, {
        params: { days: d },
        withCredentials: true,
      });
      setData(res.data);
      setDays(d);
    } catch (e) {
      toast.error("Couldn't load energy trend");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(7); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const trendIcon = (t) => {
    if (t === "up") return <TrendingUp className="w-4 h-4 text-emerald-400" />;
    if (t === "down") return <TrendingDown className="w-4 h-4 text-red-400" />;
    return <Minus className="w-4 h-4 text-zinc-400" />;
  };

  const chartData = (data?.points || []).map((p) => ({
    label: p.day_label,
    date: p.date,
    energy: p.energy_level,
    tasks: p.tasks_completed,
    mood: p.mood,
    wins: p.wins,
  }));

  const TooltipContent = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
      <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-2.5 text-xs text-zinc-100 shadow-xl max-w-[220px]">
        <div className="font-semibold mb-1">{p.label} · {p.date}</div>
        <div className="flex items-center gap-1.5">
          <span className="text-purple-300">Energy:</span>
          <span className="font-bold">{p.energy != null ? `${p.energy}/5` : "—"}</span>
          {p.mood && <span className="ml-auto">{moodEmoji(p.mood)} {p.mood}</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-emerald-300">Tasks:</span>
          <span className="font-bold">{p.tasks}</span>
        </div>
        {p.wins && <div className="mt-1.5 text-zinc-400 italic">"{p.wins.slice(0, 80)}{p.wins.length > 80 ? "…" : ""}"</div>}
      </div>
    );
  };

  return (
    <Card className="bento-card border-purple-500/30" data-testid="energy-trend-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center justify-between gap-3 flex-wrap">
          <span className="flex items-center gap-2">
            <Activity className="w-5 h-5 text-purple-400" />
            Energy & Output Trend
          </span>
          <div className="flex items-center gap-2">
            {[7, 14, 30].map((d) => (
              <button
                key={d}
                onClick={() => load(d)}
                disabled={loading}
                data-testid={`energy-window-${d}`}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors border ${
                  days === d
                    ? "bg-purple-600 text-white border-purple-500"
                    : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:bg-zinc-800"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8">
            <Loader2 className="w-4 h-4 animate-spin" /> Plotting your last {days} days...
          </div>
        )}

        {data && (
          <>
            {data.burnout_alert && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-sm" data-testid="burnout-alert">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="text-red-200">
                  <span className="font-semibold">Burnout warning.</span> 3+ days in a row with energy ≤ 2. Protect tomorrow: shorten the calendar, move your hardest task to AM, and finish by 5.
                </div>
              </div>
            )}

            {/* KPIs */}
            <div className="flex items-center gap-5 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Avg energy</span>
                <span className="font-bold text-white text-lg">
                  {data.avg_energy != null ? `${data.avg_energy}/5` : "—"}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Trend</span>
                <Badge variant="outline" className="capitalize flex items-center gap-1 border-zinc-700">
                  {trendIcon(data.energy_trend)} {data.energy_trend}
                </Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs uppercase tracking-wide text-muted-foreground">Checkouts logged</span>
                <span className="font-semibold text-white">{data.checkouts_logged}/{data.window_days}</span>
              </div>
              {data.high_day_energy_avg != null && data.low_day_energy_avg != null && (
                <div className="flex items-center gap-2 text-xs text-zinc-300">
                  <span className="uppercase tracking-wide text-muted-foreground">Output ↔ Energy:</span>
                  <span>
                    High-output days <span className="font-semibold text-emerald-300">{data.high_day_energy_avg}</span> vs low-output <span className="font-semibold text-amber-300">{data.low_day_energy_avg}</span>
                  </span>
                </div>
              )}
            </div>

            {/* Chart */}
            <div className="h-[240px]" data-testid="energy-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="label" stroke="#71717a" fontSize={11} />
                  <YAxis yAxisId="left" domain={[0, 5]} ticks={[0, 1, 2, 3, 4, 5]} stroke="#a855f7" fontSize={11} />
                  <YAxis yAxisId="right" orientation="right" stroke="#10b981" fontSize={11} allowDecimals={false} />
                  <Tooltip content={<TooltipContent />} />
                  <ReferenceLine yAxisId="left" y={3} stroke="#52525b" strokeDasharray="3 3" />
                  <Bar yAxisId="right" dataKey="tasks" fill="#10b981" fillOpacity={0.35} radius={[4, 4, 0, 0]} name="Tasks" />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="energy"
                    stroke="#a855f7"
                    strokeWidth={2.5}
                    dot={{ r: 5, fill: "#a855f7", stroke: "#18181b", strokeWidth: 2 }}
                    activeDot={{ r: 7 }}
                    connectNulls
                    name="Energy"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Mood strip */}
            <div className="flex items-center justify-between px-1 text-[11px] text-zinc-400" data-testid="mood-strip">
              {chartData.map((p) => (
                <div key={p.date} className="flex flex-col items-center gap-0.5 flex-1">
                  <span className="text-base leading-none" title={p.mood || "no mood logged"}>
                    {moodEmoji(p.mood) || "·"}
                  </span>
                  <span className="text-[10px] text-zinc-500 truncate capitalize">{p.mood || ""}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2 pt-2 border-t border-zinc-800 text-xs text-zinc-500">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
              <span>All energy, mood, and task data is read directly from your private database — no LLM, no third party.</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
